import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { slotChipMeta, type SlotOption, type SlotNorm } from '../lib/tableChooseForMe';
import { beginModalBodyScrollLock } from '../utils/modalScrollLock';

type Props = {
  open: boolean;
  onClose: () => void;
  maxParty: number;
  slotOptions: SlotOption[];
  /** When true (Zeus default), hide Preferred entry — book table for the event only. */
  omitTimeSlots?: boolean;
  busy?: boolean;
  onConfirm: (party: number, slot: SlotNorm) => void;
  /** Shown in pricing summary row (e.g. "—" or formatted fee) */
  serviceFeeText?: string;
  /** Shown in total row (e.g. "—", "At checkout", or "Free") */
  totalText?: string;
  /** e.g. `Incl. $X Fees / Incl. $Y Tax` from API `fee_preview.inclusions` */
  inclusionsSubtext?: string;
  /** Fires when party or entry slot changes (for fee preview in parent) */
  onPreviewChange?: (party: number, slotIndex: number) => void;
};

export const ChooseForMeModal: FunctionalComponent<Props> = ({
  open,
  onClose,
  maxParty,
  slotOptions,
  omitTimeSlots = false,
  busy = false,
  onConfirm,
  serviceFeeText,
  totalText,
  inclusionsSubtext,
  onPreviewChange,
}) => {
  const safeMax = Math.max(1, maxParty);
  const [party, setParty] = useState(Math.min(4, safeMax));
  const [slotIndex, setSlotIndex] = useState(0);
  const effectiveSlotOptions = omitTimeSlots
    ? [{ key: 'open', slot: null as SlotNorm, label: 'Book for event' }]
    : slotOptions;

  useEffect(() => {
    if (!open) return;
    setParty(Math.min(4, safeMax));
    setSlotIndex(0);
  }, [open, safeMax, effectiveSlotOptions.length, omitTimeSlots]);

  useEffect(() => {
    if (!open) return;
    onPreviewChange?.(party, slotIndex);
  }, [open, party, slotIndex, onPreviewChange]);

  useEffect(() => {
    if (!open) return;
    return beginModalBodyScrollLock();
  }, [open]);

  if (!open) return null;

  const selectedOpt = effectiveSlotOptions[slotIndex] ?? effectiveSlotOptions[0];
  const canSubmit = !busy && safeMax >= 1 && effectiveSlotOptions.length > 0 && selectedOpt;
  const fee = serviceFeeText ?? '—';
  const tot = totalText ?? '—';

  return (
    <div class="mw-tl-cfm-backdrop" onClick={busy ? undefined : onClose}>
      <div
        class="mw-tl-curate"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose for me"
      >
        <button type="button" class="mw-tl-curate__close" onClick={onClose} disabled={busy} aria-label="Close">
          ✕
        </button>

        <p class="mw-tl-curate__kicker">Exclusive reservation</p>
        <h2 class="mw-tl-curate__title">Choose for me</h2>
        <p class="mw-tl-curate__lede">
          {omitTimeSlots
            ? 'Let us select the best available table for your party. Seamless, then straight to checkout.'
            : 'Let us select the best available table for your party and entry time. Seamless, then straight to checkout.'}
        </p>

        <div class="mw-tl-curate__section">
          <div class="mw-tl-curate__rowhead">
            <span class="mw-tl-curate__lhead">Party size</span>
            <div class="mw-tl-curate__guest">
              <span class="mw-tl-curate__guest-num" aria-hidden>
                {String(party).padStart(2, '0')}
              </span>
              <span class="mw-tl-curate__guest-cap">guests</span>
            </div>
          </div>
          <div class="mw-tl-curate__slider-wrap">
            <input
              type="range"
              class="mw-tl-curate__range"
              min={1}
              max={safeMax}
              value={party}
              onInput={(e) => setParty(+(e.target as HTMLInputElement).value)}
              disabled={busy}
            />
            <div class="mw-tl-curate__scale" aria-hidden>
              <span>01</span>
              <span>{String(safeMax).padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        {!omitTimeSlots && effectiveSlotOptions.length > 0 && (
          <div class="mw-tl-curate__section">
            <span class="mw-tl-curate__lhead mw-tl-curate__lhead--block">Preferred entry</span>
            <div
              class={
                effectiveSlotOptions.length === 1
                  ? 'mw-tl-curate__slots mw-tl-curate__slots--one'
                  : 'mw-tl-curate__slots'
              }
              role="radiogroup"
              aria-label="Preferred entry"
            >
              {effectiveSlotOptions.map((o, i) => {
                const { main, sub } = slotChipMeta(o.slot);
                const selected = i === slotIndex;
                return (
                  <button
                    key={o.key + String(i)}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    class={selected ? 'mw-tl-curate__slot mw-tl-curate__slot--on' : 'mw-tl-curate__slot'}
                    onClick={() => setSlotIndex(i)}
                    disabled={busy}
                  >
                    <span class="mw-tl-curate__slot-time">{main}</span>
                    <span class="mw-tl-curate__slot-sub">{sub}</span>
                    <span class="mw-tl-curate__sr-only">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div class="mw-tl-curate__summary" aria-label="Price summary">
          <div class="mw-tl-curate__sumrow">
            <span>Service fee</span>
            <span class="mw-tl-curate__sumval">{fee}</span>
          </div>
          <div class="mw-tl-curate__sumrow mw-tl-curate__sumrow--total">
            <span>Total estimated</span>
            <span class="mw-tl-curate__sumtotal">{tot}</span>
          </div>
        </div>

        {inclusionsSubtext ? (
          <p class="mw-tl-curate__inclusions" style={{ fontSize: '0.8rem', color: 'var(--mw-text-muted)', margin: '0.5rem 0 0' }}>
            {inclusionsSubtext}
          </p>
        ) : null}

        <div class="mw-tl-curate__footer">
          <button
            type="button"
            class="mw-tl-curate__submit"
            disabled={!canSubmit}
            onClick={() => {
              if (!selectedOpt) return;
              onConfirm(party, selectedOpt.slot);
            }}
          >
            {busy ? 'Reserving…' : 'Continue to checkout'}
          </button>
        </div>
        <p class="mw-tl-curate__fineprint">
          {omitTimeSlots
            ? 'Smallest available table for your party size — same as on Meeteor web.'
            : 'Smallest available table for your size and this entry window — same as on Meeteor web.'}
        </p>
      </div>
    </div>
  );
};
